import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LockIcon from '@mui/icons-material/Lock';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import SpeedIcon from '@mui/icons-material/Speed';
import DescriptionIcon from '@mui/icons-material/Description';
import BuildIcon from '@mui/icons-material/Build';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import FeedbackIcon from '@mui/icons-material/Feedback';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { useTranslation } from 'react-i18next';
import { useCRUD } from '../../../../api/crud';
import { basicInfoContainerStyle, serviceActionsSectionStyle } from '../../../../styles/commonStyles';
import {
  JOB_STATUS_TRANSITION_ACTIONS_ENDPOINT,
  getJobStatusLabelKey,
  JOB_STATUS_COLOR_MAP,
  JOB_KIND_COLOR_MAP,
  getJobKindLabelKey,
} from '../../../../consts';
import { StatusTransitionRequiredAction, StatusTransitionRequiredField } from '../../../../types/jobs';
import { useHasPermission } from '../../../../contexts/auth';
import { useBreadcrumbs } from '../../../../components/ui/breadcrumps/breadcrumbsContext';
import { TENANT_ROUTES } from '../../tenantConfig.routes';
import CustomCircularProgress from '../../../../components/ui/customCircularProgress/customCircularProgress.component';

const FIELD_TYPE_ICONS: Record<string, React.ReactElement> = {
  C: <CheckBoxIcon fontSize="small" />,
  V: <SpeedIcon fontSize="small" />,
  T: <DescriptionIcon fontSize="small" />,
  S: <BuildIcon fontSize="small" />,
  I: <ReportProblemIcon fontSize="small" />,
  U: <Inventory2Icon fontSize="small" />,
  R: <FeedbackIcon fontSize="small" />,
  A: <AttachFileIcon fontSize="small" />,
};

export const TenantStatusTransitionActionDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { get } = useCRUD(JOB_STATUS_TRANSITION_ACTIONS_ENDPOINT);
  const { setOverride, clearOverride } = useBreadcrumbs();
  const [action, setAction] = useState<StatusTransitionRequiredAction | null>(null);
  const canView = useHasPermission("service.view_jobstatustransitionaction");

  useEffect(() => {
    const listPath = TENANT_ROUTES.jobStatusActions;
    setOverride(listPath, t('Job status actions'));

    const fetchAction = async () => {
      if (id) {
        const result = await get(id);
        if (result?.success && result?.data) {
          setAction(result.data);
          const prevLabel = getJobStatusLabelKey(result.data.previous_status);
          const newLabel = getJobStatusLabelKey(result.data.new_status);
          setOverride(
            TENANT_ROUTES.jobStatusAction(id),
            `${prevLabel ? t(prevLabel) : result.data.previous_status} → ${newLabel ? t(newLabel) : result.data.new_status}`
          );
        } else {
          navigate(TENANT_ROUTES.jobStatusActions);
        }
      }
    };

    const handleUpdated = () => { fetchAction(); };
    window.addEventListener('statusTransitionActionsUpdated', handleUpdated);
    fetchAction();
    return () => {
      window.removeEventListener('statusTransitionActionsUpdated', handleUpdated);
      clearOverride(listPath);
      if (id) {
        clearOverride(TENANT_ROUTES.jobStatusAction(id));
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, t]);

  if (!canView) return null;
  if (!action) return <CustomCircularProgress />;

  const prevLabelKey = getJobStatusLabelKey(action.previous_status);
  const newLabelKey = getJobStatusLabelKey(action.new_status);
  const FIELD_TYPE_LABELS: Record<string, string> = {
    C: t('Checkbox'),
    V: t('Counter values'),
    T: t('Description'),
    S: t('Services'),
    I: t('Issues'),
    U: t('Used parts'),
    R: t('Rejection reason'),
    A: t('Attachments'),
  };

  const obligatoryFields = action.required_fields.filter((field) => field.is_required);
  const optionalFields = action.required_fields.filter((field) => !field.is_required);

  const renderFieldChip = (field: StatusTransitionRequiredField) => (
    <Chip
      key={field.id}
      icon={FIELD_TYPE_ICONS[field.field_type]}
      label={FIELD_TYPE_LABELS[field.field_type] ?? field.field_type}
      size="small"
      color={field.is_required ? 'warning' : 'default'}
      variant="outlined"
    />
  );

  return (
    <Box sx={basicInfoContainerStyle}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Chip
            label={prevLabelKey ? t(prevLabelKey) : action.previous_status}
            color={JOB_STATUS_COLOR_MAP[action.previous_status] ?? 'default'}
          />
          <ArrowForwardIcon fontSize="small" color="disabled" />
          <Chip
            label={newLabelKey ? t(newLabelKey) : action.new_status}
            color={JOB_STATUS_COLOR_MAP[action.new_status] ?? 'default'}
          />
        </Box>
        {action.is_protected && (
          <Chip
            icon={<LockIcon fontSize="small" />}
            label={t('Protected default action')}
            size="small"
            color="info"
            variant="outlined"
          />
        )}
      </Box>

      <Box component="fieldset" sx={serviceActionsSectionStyle}>
        <Typography component="legend" variant="subtitle2" sx={{ px: 1, mb: 1 }}>
          {t('Obligatory fields')}
        </Typography>
        {obligatoryFields.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, px: 1 }}>
            {obligatoryFields.map(renderFieldChip)}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
            {t('No obligatory fields configured.')}
          </Typography>
        )}
      </Box>

      <Box component="fieldset" sx={serviceActionsSectionStyle}>
        <Typography component="legend" variant="subtitle2" sx={{ px: 1, mb: 1 }}>
          {t('Optional fields')}
        </Typography>
        {optionalFields.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, px: 1 }}>
            {optionalFields.map(renderFieldChip)}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
            {t('No optional fields configured.')}
          </Typography>
        )}
      </Box>

      <Box component="fieldset" sx={serviceActionsSectionStyle}>
        <Typography component="legend" variant="subtitle2" sx={{ px: 1, mb: 1 }}>
          {t('Applies to')}
        </Typography>
        {action.job_kinds.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, px: 1 }}>
            {action.job_kinds.map((jk) => (
              <Chip
                key={jk.id}
                label={t(getJobKindLabelKey(jk.kind) ?? jk.kind)}
                color={JOB_KIND_COLOR_MAP[jk.kind] ?? "default"}
                size="small"
              />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
            {t('Applies to all job kinds.')}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
